import { API_BASE_URL } from './api';
import { authService } from './authService';
import type { CheckInRequest, CreateDiveSiteListRequest, DiveSiteList, UpdateDiveSiteListRequest } from '../types/DiveSite';

export class DiveSiteListApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'DiveSiteListApiError';
    this.status = status;
  }
}

const throwDiveSiteListApiError = async (response: Response, fallbackMessage: string): Promise<never> => {
  const payload = await response.json().catch(() => null);
  throw new DiveSiteListApiError(payload?.error || fallbackMessage, response.status);
};

class DiveSiteListService {
  async getMyDiveSiteLists(): Promise<DiveSiteList[]> {
    const response = await fetch(`${API_BASE_URL}/dive-site-lists/mine`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      await throwDiveSiteListApiError(response, 'Failed to fetch dive site lists');
    }

    return await response.json();
  }

  async getDiveSiteList(listId: string): Promise<DiveSiteList> {
    const response = await fetch(`${API_BASE_URL}/dive-site-lists/${encodeURIComponent(listId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      await throwDiveSiteListApiError(response, 'Failed to fetch dive site list');
    }

    return await response.json();
  }

  async getSharedDiveSiteList(shareCode: string): Promise<DiveSiteList> {
    const response = await fetch(`${API_BASE_URL}/dive-site-lists/shared/${encodeURIComponent(shareCode)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      await throwDiveSiteListApiError(response, 'Failed to fetch shared dive site list');
    }

    return await response.json();
  }

  async createDiveSiteList(request: CreateDiveSiteListRequest): Promise<DiveSiteList> {
    const response = await fetch(`${API_BASE_URL}/dive-site-lists`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
      body: JSON.stringify({
        name: request.name,
        description: request.description,
        siteIds: request.siteIds ?? [],
        visibility: request.visibility ?? 'private',
        collaboratorUserIds: request.collaboratorUserIds ?? [],
        siteSchedules: request.siteSchedules ?? {},
      }),
    });

    if (!response.ok) {
      await throwDiveSiteListApiError(response, 'Failed to create dive site list');
    }

    return await response.json();
  }

  async updateDiveSiteList(request: UpdateDiveSiteListRequest): Promise<DiveSiteList> {
    const response = await fetch(`${API_BASE_URL}/dive-site-lists/${request.listId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
      body: JSON.stringify({
        name: request.name,
        description: request.description,
        siteIds: request.siteIds ?? [],
        visibility: request.visibility ?? 'private',
        collaboratorUserIds: request.collaboratorUserIds ?? [],
        siteSchedules: request.siteSchedules ?? {},
      }),
    });

    if (!response.ok) {
      await throwDiveSiteListApiError(response, 'Failed to update dive site list');
    }

    return await response.json();
  }

  async deleteDiveSiteList(listId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/dive-site-lists/${listId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      await throwDiveSiteListApiError(response, 'Failed to delete dive site list');
    }
  }

  async checkIn(listId: string, request: CheckInRequest): Promise<DiveSiteList> {
    const response = await fetch(`${API_BASE_URL}/dive-site-lists/${encodeURIComponent(listId)}/check-in`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
      body: JSON.stringify({ siteId: request.siteId, checkedIn: request.checkedIn }),
    });

    if (!response.ok) {
      await throwDiveSiteListApiError(response, 'Failed to update check-in status');
    }

    return await response.json();
  }
}

export const diveSiteListService = new DiveSiteListService();